import { inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
} from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { BreadcrumbItem, ViewHeaderConfig } from '../routing/view-header.types';

const DEFAULT_DOCUMENT_TITLE = 'Jobboard | catchHR';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class PlatformViewHeaderService {
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  private readonly translateService = inject(TranslocoService);

  readonly breadcrumbs = signal<BreadcrumbItem[]>([]);
  readonly title = signal<string | null>(null);

  private documentTitleSubscription: Subscription | null = null;
  private lastPageTitleKey: string | null = null;

  constructor() {
    this.syncBreadcrumbsFromRoute();

    this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.syncBreadcrumbsFromRoute();
      }
    });

    this.translateService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.lastPageTitleKey) {
        this.updateDocumentTitle(this.lastPageTitleKey);
      }
    });
  }

  /**
   * Reads breadcrumb config from the active leaf route.
   */
  private syncBreadcrumbsFromRoute(): void {
    const leaf = this.leafRouteSnapshot(this.router.routerState.snapshot.root);
    const viewHeader = leaf.data['viewHeader'] as ViewHeaderConfig | undefined;
    this.breadcrumbs.set(viewHeader?.breadcrumbs ?? []);
    this.title.set(viewHeader?.title ?? null);
    this.updateDocumentTitle(viewHeader?.pageTitle ?? viewHeader?.title ?? null);
  }

  /**
   * Sets breadcrumbs manually for a view.
   * @param breadcrumbs - Breadcrumb items to display
   */
  setBreadcrumbs(breadcrumbs: BreadcrumbItem[]): void {
    this.breadcrumbs.set(breadcrumbs);
  }

  /**
   * Updates the browser tab title from an i18n key, or resets to the default.
   * @param translationKey - Transloco key for the page title, or null to reset
   */
  private updateDocumentTitle(translationKey: string | null): void {
    this.documentTitleSubscription?.unsubscribe();
    this.documentTitleSubscription = null;
    this.lastPageTitleKey = translationKey;

    if (!translationKey) {
      this.titleService.setTitle(DEFAULT_DOCUMENT_TITLE);
      return;
    }

    this.documentTitleSubscription = this.translateService
      .selectTranslate(translationKey)
      .pipe(untilDestroyed(this))
      .subscribe((viewTitle) => {
        this.titleService.setTitle(`${viewTitle} | catchHR`);
      });
  }

  /**
   * Finds the deepest activated route snapshot.
   * @param root - Root route snapshot
   * @returns Leaf route snapshot
   */
  private leafRouteSnapshot(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let route = root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }
}
